import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const label = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Fast capture first: a first name is the only thing asked for. Everything else lives
 * behind "Add more details", so a contact can be saved in seconds and enriched later.
 */
export default function ContactFormDialog({ open, onOpenChange, enums, onSaved, contact }) {
  const editing = Boolean(contact);
  const [form, setForm] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setExpanded(editing);
    setDupes([]);
    setForm(editing
      ? {
        first_name: contact.first_name || "", last_name: contact.last_name || "",
        preferred_name: contact.preferred_name || "",
        contact_types: contact.contact_types || [],
        relationship_status: contact.relationship_status || "new",
        how_we_met: contact.relationship_context?.how_we_met || "",
        cadence_days: contact.relationship_context?.cadence_days || 0,
      }
      : { first_name: "", last_name: "", contact_types: [], relationship_status: "new",
        email: "", phone: "", city: "", org_name: "", title: "", how_we_met: "",
        note: "", cadence_days: 0 });
  }, [open, editing, contact]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleType = (t) => setForm((f) => ({
    ...f,
    contact_types: f.contact_types?.includes(t)
      ? f.contact_types.filter((x) => x !== t) : [...(f.contact_types || []), t],
  }));

  // Duplicate warning against the owner's OWN records only (never a global lookup).
  useEffect(() => {
    if (editing || !open) return;
    const name = `${form.first_name || ""} ${form.last_name || ""}`.trim();
    if (!form.email && name.length < 3) { setDupes([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/crm/match", { params: { email: form.email || "", name } });
        setDupes(data.matches || []);
      } catch { setDupes([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.first_name, form.last_name, form.email, editing, open]);

  const canSave = useMemo(
    () => Boolean((form.first_name || "").trim() || (form.preferred_name || "").trim()),
    [form.first_name, form.preferred_name],
  );

  const save = async () => {
    setSaving(true);
    try {
      const base = {
        first_name: form.first_name, last_name: form.last_name,
        preferred_name: form.preferred_name, contact_types: form.contact_types,
        relationship_status: form.relationship_status,
        relationship_context: { how_we_met: form.how_we_met,
          cadence_days: Number(form.cadence_days) || 0 },
      };
      if (editing) {
        await api.patch(`/crm/contacts/${contact.id}`, base);
      } else {
        const methods = [];
        if (form.email) methods.push({ kind: "email", value: form.email, label: "work", preferred: true });
        if (form.phone) methods.push({ kind: "phone", value: form.phone, label: "mobile", preferred: true });
        if (form.city) methods.push({ kind: "address", label: "work", preferred: true, address: { city: form.city } });
        await api.post("/crm/contacts", {
          ...base, methods,
          orgs: form.org_name ? [{ org_name: form.org_name, title: form.title, current: true }] : [],
          note: form.note || undefined,
        });
      }
      toast.success(editing ? "Contact updated" : "Contact added");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const code = e?.response?.data?.detail;
      toast.error(code === "crm_first_name_required" ? "A first name is required"
        : code === "crm_contact_ceiling_reached" ? "You've reached your contact limit"
          : "Couldn't save this contact");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="contact-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit contact" : "Add contact"}</DialogTitle>
          <DialogDescription>
            A first name is all you need — everything else is optional and can be added later.
            This contact is private to you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cf-first">First name</Label>
              <Input id="cf-first" data-testid="contact-first-name" value={form.first_name || ""}
                onChange={set("first_name")} maxLength={75} autoFocus />
            </div>
            <div>
              <Label htmlFor="cf-last">Last name</Label>
              <Input id="cf-last" data-testid="contact-last-name" value={form.last_name || ""}
                onChange={set("last_name")} maxLength={75} />
            </div>
          </div>

          {!editing && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="cf-email">Email</Label>
                <Input id="cf-email" data-testid="contact-email" type="email"
                  value={form.email || ""} onChange={set("email")} maxLength={254} />
              </div>
              <div>
                <Label htmlFor="cf-phone">Phone</Label>
                <Input id="cf-phone" data-testid="contact-phone" value={form.phone || ""}
                  onChange={set("phone")} maxLength={40} />
              </div>
            </div>
          )}

          {dupes.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="contact-duplicate-warning">
              <span className="font-[600]">You may already have this person:</span>{" "}
              {dupes.map((d) => d.display_name).join(", ")}. Saving creates a separate contact.
            </div>
          )}

          {!expanded ? (
            <button type="button" onClick={() => setExpanded(true)}
              data-testid="contact-expand-details"
              className="inline-flex items-center gap-1.5 text-sm font-[560] text-primary hover:underline">
              <Plus className="h-4 w-4" /> Add more details
            </button>
          ) : (
            <div className="space-y-4 border-t border-border pt-4" data-testid="contact-advanced-fields">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="cf-preferred">Preferred name</Label>
                  <Input id="cf-preferred" data-testid="contact-preferred-name"
                    value={form.preferred_name || ""} onChange={set("preferred_name")} maxLength={75} />
                </div>
                {!editing && (
                  <div>
                    <Label htmlFor="cf-city">City</Label>
                    <Input id="cf-city" data-testid="contact-city" value={form.city || ""}
                      onChange={set("city")} maxLength={80} />
                  </div>
                )}
              </div>
              {!editing && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="cf-org">Organization</Label>
                    <Input id="cf-org" data-testid="contact-org" value={form.org_name || ""}
                      onChange={set("org_name")} maxLength={150} />
                  </div>
                  <div>
                    <Label htmlFor="cf-title">Title</Label>
                    <Input id="cf-title" data-testid="contact-title" value={form.title || ""}
                      onChange={set("title")} maxLength={100} />
                  </div>
                </div>
              )}
              <div>
                <span className="mb-1.5 block text-sm font-[560]">Contact type</span>
                <div className="flex flex-wrap gap-1.5">
                  {(enums.contact_types || []).map((t) => (
                    <button key={t} type="button" onClick={() => toggleType(t)}
                      aria-pressed={form.contact_types?.includes(t)}
                      data-testid={`contact-type-${t}`}
                      className={`rounded-full px-3 py-1 text-sm transition-colors ${
                        form.contact_types?.includes(t)
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-foreground/70 hover:bg-secondary/80"}`}>
                      {label(t)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="cf-status">Relationship status</Label>
                  <select id="cf-status" data-testid="contact-status" value={form.relationship_status || "new"}
                    onChange={set("relationship_status")}
                    className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    {(enums.relationship_statuses || []).map((s) => (
                      <option key={s} value={s}>{label(s)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="cf-cadence">Stay in touch every (days)</Label>
                  <Input id="cf-cadence" data-testid="contact-cadence" type="number" min="0" max="3650"
                    value={form.cadence_days || 0} onChange={set("cadence_days")} />
                </div>
              </div>
              <div>
                <Label htmlFor="cf-met">How we met</Label>
                <Textarea id="cf-met" data-testid="contact-how-we-met" rows={2} maxLength={2000}
                  value={form.how_we_met || ""} onChange={set("how_we_met")} />
              </div>
              {!editing && (
                <div>
                  <Label htmlFor="cf-note">Private note</Label>
                  <Textarea id="cf-note" data-testid="contact-note" rows={3} maxLength={10000}
                    value={form.note || ""} onChange={set("note")}
                    placeholder="Only you can see your notes." />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="contact-form-cancel">
            <X className="mr-1.5 h-4 w-4" /> Cancel
          </Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="contact-form-save">
            {saving ? "Saving…" : editing ? "Save changes" : "Add contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
